import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

interface User {
  id: number;
  username: string;
  email?: string;
  full_name?: string;
  role: string;
  is_verified: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (accessToken: string, piUser?: any) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Configure axios base URL
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8002';
    axios.defaults.baseURL = apiUrl;
    console.log('API Base URL configured:', apiUrl);
    console.log('Environment:', process.env.NODE_ENV);
  }, []);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          // Check if it's a mock/test token
          if (token.includes('mock_jwt') || token.includes('offline_jwt') || token.includes('test_token')) {
            console.log('Using stored test/mock session');
            // Restore mock user from localStorage or create default
            const storedUser = localStorage.getItem('mockUser');
            if (storedUser) {
              setUser(JSON.parse(storedUser));
            } else {
              // Create default mock user
              const mockUser = {
                id: 1,
                username: 'TestUser',
                role: 'user',
                is_verified: true
              };
              setUser(mockUser);
              localStorage.setItem('mockUser', JSON.stringify(mockUser));
            }
          } else {
            // Try to validate real token with backend (with timeout)
            try {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
              
              const response = await axios.get('/api/v1/auth/me', {
                signal: controller.signal
              });
              clearTimeout(timeoutId);
              setUser(response.data.data);  // Backend returns { success: true, data: {...} }
            } catch (error: any) {
              console.warn('Failed to validate token with backend:', error.message);
              // Don't logout immediately - keep the session in offline mode
              const storedUser = localStorage.getItem('mockUser');
              if (storedUser) {
                try {
                  setUser(JSON.parse(storedUser));
                  console.log('Restored user from localStorage');
                } catch (parseError) {
                  console.error('Failed to parse stored user:', parseError);
                  logout();
                }
              } else {
                // Only logout if we can't restore any user data
                console.log('No stored user, logging out');
                logout();
              }
            }
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          // Don't block the app - just set loading to false
        }
      }
      setLoading(false);
    };

    // Wrap initAuth in try-catch to prevent blocking
    try {
      initAuth();
    } catch (error) {
      console.error('Critical auth initialization error:', error);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (piAccessToken: string, piUser?: any) => {
    try {
      setLoading(true);
      
      console.log('Login attempt with:', {
        accessToken: piAccessToken,
        user: piUser || {}
      });
      
      // For testing purposes, simulate successful login without backend
      if (piAccessToken.includes('test_token') || piAccessToken.includes('dev_pi_token')) {
        console.log('Using offline test mode');
        
        // Create mock JWT token for local testing
        const mockToken = 'mock_jwt_' + Date.now();
        const mockUser = {
          id: 1,
          username: piUser?.username || 'TestUser',
          pi_uid: piUser?.uid || 'test_uid',
          role: 'user',
          is_verified: true
        };
        
        setToken(mockToken);
        setUser(mockUser);
        localStorage.setItem('token', mockToken);
        localStorage.setItem('mockUser', JSON.stringify(mockUser));
        
        toast.success('Successfully logged in (Test Mode)!');
        return;
      }
      
      // Try backend authentication
      try {
        const response = await axios.post('/api/v1/auth/pi-auth', {
          accessToken: piAccessToken,
          user: piUser || {}
        });

        console.log('Backend response:', response.data);
        const { data } = response.data;
        
        setToken(data.access_token);
        setUser(data.user);
        localStorage.setItem('token', data.access_token);
        
        toast.success('Successfully logged in with Pi Network!');
      } catch (backendError: any) {
        console.warn('Backend unavailable, using test mode:', backendError.message);
        
        // Fallback to test mode if backend is unavailable
        const mockToken = 'offline_jwt_' + Date.now();
        const mockUser = {
          id: 1,
          username: piUser?.username || 'TestUser',
          pi_uid: piUser?.uid || 'test_uid',
          role: 'user',
          is_verified: true
        };
        
        setToken(mockToken);
        setUser(mockUser);
        localStorage.setItem('token', mockToken);
        localStorage.setItem('mockUser', JSON.stringify(mockUser));
        
        toast.success('Successfully logged in (Offline Mode)!');
      }
    } catch (error: any) {
      console.error('Pi Network login failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(error.response?.data?.detail || 'Pi Network authentication failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('mockUser');
    delete axios.defaults.headers.common['Authorization'];
    toast.info('Logged out successfully');
  };

  const refreshToken = async () => {
    try {
      const response = await axios.post('/api/v1/auth/refresh');
      const { access_token } = response.data;
      
      setToken(access_token);
      localStorage.setItem('token', access_token);
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    logout,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};